import * as React from "react";
import { makeStyles, createStyles, Theme, Typography } from "@material-ui/core";
import ReactMarkdown from "react-markdown";
import Section from "../Section";
import {
  SECTION_WHITE,
  TEXT_BODY,
  TEXT_HEADING,
  SECTION_PADDING_Y,
  SECTION_PANEL_NAVY,
} from "../../styles/homepageSections";

export type ComparisonColumnData = {
  name: string;
  highlighted?: boolean | null;
};

export type ComparisonRowData = {
  capability: string;
  cells?: { value?: string | null }[] | null;
};

export type ToolComparisonData = {
  subheadline?: string | null;
  headline?: string | null;
  description?: string | null;
  capabilityColumnLabel?: string | null;
  columns?: ComparisonColumnData[] | null;
  rows?: ComparisonRowData[] | null;
};

const hasText = (value?: string | null) =>
  Boolean(value && value.replace(/<[^>]*>/g, "").trim());

const ROW_BORDER = "rgba(32, 35, 56, 0.12)";

const useStyles = makeStyles((t: Theme) =>
  createStyles({
    section: {
      padding: SECTION_PADDING_Y.mobile,
      backgroundColor: SECTION_WHITE,
      [t.breakpoints.up("md")]: {
        padding: SECTION_PADDING_Y.desktop,
      },
    },
    header: {
      maxWidth: 900,
      marginBottom: "2.5rem",
      [t.breakpoints.up("md")]: {
        marginBottom: "3rem",
      },
    },
    subheadline: {
      color: t.palette.primary.main,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      marginBottom: 12,
      [t.breakpoints.up("md")]: {
        fontSize: 13,
        marginBottom: 16,
      },
    },
    headline: {
      color: TEXT_HEADING,
      fontWeight: 600,
      fontSize: 32,
      lineHeight: 1.25,
      margin: 0,
      padding: "0 0 2rem !important",
      [t.breakpoints.up("md")]: {
        fontSize: 44,
        lineHeight: 1.2,
      },
    },
    headlineAfterSubheadline: {
      paddingTop: "0 !important",
      marginTop: 0,
    },
    description: {
      color: TEXT_BODY,
      fontSize: 18,
      lineHeight: 1.55,
      "& p": {
        margin: "0 0 1em",
      },
      "& p:last-child": {
        marginBottom: 0,
      },
      "& strong": {
        color: t.palette.primary.main,
        fontWeight: 600,
      },
      [t.breakpoints.up("md")]: {
        fontSize: 20,
        lineHeight: 1.6,
      },
    },
    tableScroll: {
      width: "100%",
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
    },
    table: {
      width: "100%",
      tableLayout: "fixed",
      borderCollapse: "separate",
      borderSpacing: 0,
      borderRadius: 12,
      overflow: "hidden",
      boxShadow: "0 4px 24px rgba(32, 35, 56, 0.08)",
    },
    th: {
      backgroundColor: SECTION_PANEL_NAVY,
      color: "#fff",
      fontSize: 9,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      padding: "12px 6px",
      textAlign: "center",
      verticalAlign: "middle",
      borderBottom: `1px solid ${ROW_BORDER}`,
      wordBreak: "break-word",
      hyphens: "auto",
      [t.breakpoints.up("sm")]: {
        fontSize: 10,
        padding: "14px 10px",
        letterSpacing: "0.08em",
      },
      [t.breakpoints.up("md")]: {
        fontSize: 11,
        padding: "16px 20px",
      },
    },
    thCapability: {
      textAlign: "left",
      [t.breakpoints.down("md")]: {
        width: "42%",
        maxWidth: "42%",
      },
      [t.breakpoints.up("md")]: {
        width: "36%",
      },
    },
    thValue: {
      width: "auto",
    },
    thValueSecond: {
      [t.breakpoints.down("md")]: {
        width: "26%",
        maxWidth: "26%",
      },
    },
    thValueCompact: {
      [t.breakpoints.down("md")]: {
        width: "16%",
        maxWidth: "16%",
      },
    },
    thHighlighted: {
      backgroundColor: "#fff",
      color: t.palette.primary.main,
      boxShadow: "inset 0 0 0 2px rgba(234, 22, 116, 0.15)",
    },
    td: {
      padding: "12px 6px",
      fontSize: 13,
      lineHeight: 1.4,
      verticalAlign: "middle",
      borderBottom: `1px solid ${ROW_BORDER}`,
      backgroundColor: "#fff",
      [t.breakpoints.up("sm")]: {
        padding: "16px 10px",
        fontSize: 14,
      },
      [t.breakpoints.up("md")]: {
        padding: "20px",
        fontSize: 15,
        lineHeight: 1.45,
      },
    },
    tdCapability: {
      color: TEXT_BODY,
      textAlign: "left",
      fontWeight: 400,
      wordBreak: "break-word",
      hyphens: "auto",
      [t.breakpoints.down("md")]: {
        width: "42%",
        maxWidth: "42%",
      },
      [t.breakpoints.up("md")]: {
        width: "36%",
      },
    },
    tdValueSecond: {
      [t.breakpoints.down("md")]: {
        width: "26%",
        maxWidth: "26%",
      },
    },
    tdValueCompact: {
      [t.breakpoints.down("md")]: {
        width: "16%",
        maxWidth: "16%",
      },
    },
    tdValue: {
      textAlign: "center",
      fontSize: 22,
      lineHeight: 1,
      color: "#9aa3b2",
      width: "auto",
      paddingLeft: 4,
      paddingRight: 4,
      [t.breakpoints.up("sm")]: {
        fontSize: 24,
      },
      [t.breakpoints.up("md")]: {
        fontSize: 28,
        paddingLeft: 20,
        paddingRight: 20,
      },
    },
    tdHighlighted: {
      backgroundColor: "#fdf8fb",
      fontWeight: 600,
    },
    tdValueHighlighted: {
      color: t.palette.primary.main,
      fontSize: 24,
      [t.breakpoints.up("sm")]: {
        fontSize: 28,
      },
      [t.breakpoints.up("md")]: {
        fontSize: 32,
      },
    },
    trLast: {
      "& td": {
        borderBottom: "none",
      },
    },
  })
);

const ToolComparisonSection: React.FC<{ data?: ToolComparisonData | null }> = ({
  data,
}) => {
  const classes = useStyles();

  if (!data) {
    return null;
  }

  const columns = data.columns?.filter((c) => c?.name) ?? [];
  const rows = data.rows?.filter((r) => r?.capability) ?? [];
  const capabilityLabel = data.capabilityColumnLabel?.trim() || "CAPABILITY";

  const hasCopy =
    hasText(data.subheadline) ||
    hasText(data.headline) ||
    hasText(data.description);
  const hasTable = columns.length > 0 && rows.length > 0;

  if (!hasCopy && !hasTable) {
    return null;
  }

  return (
    <Section color="#ffffff" className={classes.section}>
      {hasCopy && (
        <div className={classes.header}>
          {hasText(data.subheadline) && (
            <Typography component="p" className={classes.subheadline}>
              {data.subheadline}
            </Typography>
          )}
          {hasText(data.headline) && (
            <Typography
              variant="h2"
              component="h2"
              className={[
                classes.headline,
                hasText(data.subheadline) && classes.headlineAfterSubheadline,
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {data.headline}
            </Typography>
          )}
          {hasText(data.description) && (
            <div className={classes.description}>
              <ReactMarkdown
                linkTarget="_blank"
                components={{
                  p: ({ children }) => <p>{children}</p>,
                  strong: ({ children }) => <strong>{children}</strong>,
                }}
              >
                {data.description!}
              </ReactMarkdown>
            </div>
          )}
        </div>
      )}

      {hasTable && (
        <div className={classes.tableScroll}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={[classes.th, classes.thCapability].join(" ")}>
                  {capabilityLabel}
                </th>
                {columns.map((col, i) => (
                  <th
                    key={`${col.name}-${i}`}
                    className={[
                      classes.th,
                      classes.thValue,
                      i === 0 && classes.thValueSecond,
                      i >= 1 && classes.thValueCompact,
                      col.highlighted && classes.thHighlighted,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {col.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => {
                const isLast = rowIndex === rows.length - 1;
                const cells = row.cells ?? [];

                return (
                  <tr
                    key={`${row.capability}-${rowIndex}`}
                    className={isLast ? classes.trLast : undefined}
                  >
                    <td className={[classes.td, classes.tdCapability].join(" ")}>
                      {row.capability}
                    </td>
                    {columns.map((col, colIndex) => {
                      const cellValue = cells[colIndex]?.value ?? "";
                      const highlighted = col.highlighted;

                      return (
                        <td
                          key={`${rowIndex}-${colIndex}`}
                          className={[
                            classes.td,
                            classes.tdValue,
                            colIndex === 0 && classes.tdValueSecond,
                            colIndex >= 1 && classes.tdValueCompact,
                            highlighted && classes.tdHighlighted,
                            highlighted && classes.tdValueHighlighted,
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          {cellValue}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
};

export default ToolComparisonSection;
